import { SymbolConfigData } from "@lib/titan2-slingo/dist/api/model/data/SymbolConfigData";
import { TweenConstants } from "@lib/titan2-slingo/dist/game/view/abstract/constant/TweenConstants";
import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { MouseBlocker } from "@lib/titan2/dist/game/control/MouseBlocker";
import { TitanPanel } from "@lib/titan2/dist/game/control/TitanPanel";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { ResizeEvent } from "@lib/titan2/dist/game/renderer/Renderer";
import { ArrayUtil } from "@lib/titan2/dist/util/collection/ArrayUtil";
import { KeyValuePair } from "@lib/titan2/dist/util/collection/KeyValuePair";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { ViewUtils } from "@lib/titan2/dist/util/view/ViewUtils";
import { Emitter, LinkedListContainer } from "@pixi/particle-emitter";
import { gsap } from "gsap";
import { GlowFilter } from "pixi-filters";
import { Point } from "pixi.js";
import { GameEvent } from "../../../controller/event/GameEvent";
import { Firework } from "../../../utils/Firework";
import { GameMathUtil } from "../../../utils/GameMathUtil";
import { GameSymbolUtil } from "../../../utils/GameSymbolUtil";
import { TweenUtil } from "../../../utils/TweenUtil";
import { GameLang } from "../lang/GameLang";
import { GameSoundEvent } from "../../../controller/event/GameSoundEvent";
export class GameWinLineCelebration extends AbstractGameView {
    create() {
        super.create();
        Logger.addObjectRef(this, "GameWinLineCelebration");
        const numberOfReels = this.model.getReelDisplayData().getNumberOfReels();
        this.mouseBlocker = new MouseBlocker(this.landscapeProperties.mouseBlockerProperties, this.portraitProperties.mouseBlockerProperties);
        this.mouseBlocker.onClick(() => this.timelineAnimation?.seek(this.timelineAnimation.duration(), false), this);
        this.addChild(this.mouseBlocker);
        // Create emitters for each symbol.
        this.fireworkEmitters = [];
        this.fireworkFilters = [];
        for (let i = 0; i < numberOfReels; ++i) {
            const particleContainer = new LinkedListContainer();
            const glowFilter = new GlowFilter({
                distance: this.uiProperties.fireworkGlowDistance,
                outerStrength: this.uiProperties.fireworkGlowStrength,
            });
            particleContainer.filters = [glowFilter];
            this.addChild(particleContainer);
            const emitter = new Emitter(particleContainer, this.uiProperties.fireworkEmitterProperties);
            this.fireworkEmitters.push(emitter);
            this.fireworkFilters.push(glowFilter);
        }
        // Create firework symbols for each reel.
        this.fireworkPanel = TitanPanel.createPanel(this, "firework panel", this.landscapeProperties.fireworkPanelProperties, this.portraitProperties.fireworkPanelProperties);
        this.fireworkSpines = Array.from({ length: numberOfReels }, (_, i) => {
            const spine = TitanSpine.createSpine(this.fireworkPanel, `firework symbol ${i}`, this.landscapeProperties.fireworkSymbolSpineProperties, this.portraitProperties.fireworkSymbolSpineProperties);
            return spine;
        });
        this.borderSprite = TitanSprite.createSprite(this, "border", this.landscapeProperties.borderProperties, this.portraitProperties.borderProperties);
        this.lineBorderSprite = TitanSprite.createSprite(this, "line border", this.landscapeProperties.lineBorderProperties, this.portraitProperties.lineBorderProperties);
        // Create line symbols for each reel.
        this.linePanel = TitanPanel.createPanel(this, "line panel", this.landscapeProperties.linePanelProperties, this.portraitProperties.linePanelProperties);
        this.lineSpines = Array.from({ length: numberOfReels }, (_, i) => {
            const spine = TitanSpine.createSpine(this.linePanel, `line symbol ${i}`, this.landscapeProperties.lineSymbolSpineProperties, this.portraitProperties.lineSymbolSpineProperties);
            return spine;
        });
        this.amountText = ViewUtils.createField(this, "amount", this.landscapeProperties.amountTextProperties, this.portraitProperties.amountTextProperties);
        this.multiplierText = ViewUtils.createField(this, "multiplier", this.landscapeProperties.multiplierTextProperties, this.portraitProperties.multiplierTextProperties);
    }
    postCreate() {
        super.postCreate();
        this.reset();
    }
    reset() {
        this.visible = false;
        this.alpha = 0;
        this.multiplierText.alpha = 0;
        this.amountText.alpha = 0;
        this.lineBorderSprite.alpha = 0;
        this.linePanel.removeAll();
        this.fireworkPanel.removeAll();
    }
    destroy() {
        super.destroy();
        // Un-parented symbol spines won't have been destroyed automatically.
        [...this.fireworkSpines, ...this.lineSpines]
            .filter(spine => !spine.destroyed)
            .forEach(spine => spine.destroy());
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(ResizeEvent.POST_ROTATE, this.handleRecoverAnimation);
        this.registerGame(GameEvent.WIN_LINE_ANIMATION, this.handleWinLineAnimation);
    }
    handleRotate(event) {
        super.handleRotate(event);
        if (this.parent) {
            this.handleRecoverAnimation();
        }
    }
    handleRecoverAnimation() {
        TweenUtil.recover(this.timelineAnimation);
    }
    async handleWinLineAnimation() {
        this.visible = true;
        this.mouseBlocker.interactiveChildren = false;
        await this.showWinLine(this.model.getWinLine());
        await new Promise(resolve => this.callAfter(1000, resolve));
        await this.hideWinLine();
        this.reset();
    }
    async showWinLine(winLine) {
        await gsap.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.5 });
        this.timelineAnimation?.kill();
        this.timelineAnimation = gsap.timeline()
            .add(this.showMultiplier(winLine))
            .add(this.showLineSymbols(winLine))
            .add(this.jumpLineSymbols(winLine), "<")
            .add(this.showAmount(winLine), "<");
        this.mouseBlocker.alpha = 1;
        this.mouseBlocker.interactiveChildren = true;
        await this.timelineAnimation;
        this.mouseBlocker.interactiveChildren = false;
        // Only show fireworks if the payout is above the stake.
        const winLineMultipliers = this.model.getFeatureConfig().getWinLineFeatureConfig().getMultipliers(winLine.getSymbolType());
        if (winLineMultipliers[winLine.getNumberOfSymbols() - 1] > 1) {
            this.timelineAnimation = this.showFireworkSymbols(winLine);
            await this.timelineAnimation;
        }
    }
    async hideWinLine() {
        await gsap.to(this, { alpha: 0, duration: 0.5 });
    }
    showMultiplier(winLine) {
        // Update the multiplier text based on the symbol type and number of symbols in the win line.
        const multiplier = this.model.getFeatureConfig().getWinLineFeatureConfig().getMultipliers(winLine.getSymbolType())[winLine.getNumberOfSymbols() - 1] ?? 0;
        const multiplierMessage = this.getMultiplierMessage(multiplier);
        this.multiplierText.setText(multiplierMessage, true);
        // Fade the multiplier text in.
        return gsap.timeline()
            .fromTo(this.multiplierText, { alpha: 0 }, { alpha: 1, duration: 0.5 });
    }
    getMultiplierMessage(multiplier) {
        return this.langManager.getLang(GameLang.WIN_LINE_MULTIPLIER, [new KeyValuePair(GameLang.PARAM_AMOUNT, `${multiplier}x`)]);
    }
    showAmount(winLine) {
        // Fade the amount text in, counting up beyond the fade.
        const countUp = { progress: 0, target: winLine.getAmount() };
        this.amountText.setText("", true);
        this.dispatchGame(GameSoundEvent.WIN_LINE_COUNT_UP);
        return gsap.timeline()
            .fromTo(this.amountText, { alpha: 0 }, { alpha: 1, duration: 0.5 })
            .to(countUp, {
            progress: 1, duration: 1, onUpdate: () => {
                const amountMessage = this.getAmountMessage(countUp.progress * countUp.target);
                this.amountText.setText(amountMessage, true);
            }
        }, "<")
            .call(() => this.dispatchGame(GameSoundEvent.WIN_LINE_COUNT_UP_END))
            .to(this.amountText.getPixiText(), { pixi: { scale: 1.1 }, duration: TweenConstants.YOYO_DURATION, yoyo: true, repeat: 1 });
    }
    getAmountMessage(amount) {
        return this.formatCurrency(amount);
    }
    showLineSymbols(winLine) {
        const symbols = this.getLineSymbolsToDisplay(winLine);
        // Reskin and rearrange the line symbols.
        this.linePanel.removeAll();
        for (let i = 0; i < symbols.length; ++i) {
            const symbol = symbols[i];
            const symbolSpine = this.lineSpines[i];
            if (symbol.isSuperJoker()) {
                GameSymbolUtil.setSuperJokerSkin(symbolSpine);
            }
            else if (symbol.isJoker()) {
                GameSymbolUtil.setJokerSkin(symbolSpine);
            }
            else {
                const variant = GameSymbolUtil.numberToVariant(parseInt(symbol.getId(), 10));
                GameSymbolUtil.setVariantSkin(symbolSpine, variant);
            }
            this.linePanel.addToPanel(symbolSpine, false);
        }
        this.linePanel.arrange();
        // Fade the symbols in staggered.
        const symbolSpines = this.lineSpines.slice(0, symbols.length);
        return gsap.timeline()
            .fromTo(this.lineBorderSprite, { alpha: 0 }, { alpha: 1, duration: 0.25 })
            .fromTo(symbolSpines, { alpha: 0 }, { alpha: 1, duration: 0.25, stagger: 0.1 });
    }
    jumpLineSymbols(winLine) {
        // Make the symbols jump up and down in a wave.
        const symbolSpines = this.lineSpines.slice(0, winLine.getNumberOfSymbols());
        return gsap.timeline()
            .to(symbolSpines, { y: "-=15", duration: 0.25, ease: "power2.out", stagger: 0.1 })
            .to(symbolSpines, { y: "+=15", duration: 0.25, ease: "power2.in", stagger: 0.1 }, "<+=0.25");
    }
    /**
     * Determine the symbols to display for the win line.
     * - If the win line has numbers, it will show only that number variant for the whole line with no jokers.
     * - If the win line is just jokers, it will show the symbols as they are.
     */
    getLineSymbolsToDisplay(winLine) {
        return winLine.getSymbolType().isNumber()
            ? this.getMatchingNumberSymbolsToDisplay(winLine)
            : this.model.getWinLineSymbols(winLine);
    }
    showFireworkSymbols(winLine) {
        // For each symbol, fire a firework upwards with slight randomisation.
        const symbols = this.getFireworkSymbolsToDisplay(winLine);
        const variants = symbols.map(symbol => GameSymbolUtil.numberToVariant(parseInt(symbol.getId(), 10)));
        // Rearrange the panel for the symbols.
        this.fireworkPanel.removeAll();
        for (let i = 0; i < symbols.length; ++i) {
            this.fireworkPanel.addToPanel(this.fireworkSpines[i], false);
        }
        this.fireworkPanel.arrange();
        const timeline = gsap.timeline();
        for (let i = 0; i < symbols.length; ++i) {
            const fireworkSpine = this.fireworkSpines[i];
            const fireworkEmitter = this.fireworkEmitters[i];
            const fireworkFilter = this.fireworkFilters[i];
            const firework = new Firework(fireworkSpine, fireworkEmitter);
            // Remove the firework spine from the panel so it doesn't get rearranged on resize.
            const localPosition = this.toLocal(fireworkSpine.parent.toGlobal(fireworkSpine.position));
            fireworkSpine.position.copyFrom(localPosition);
            this.fireworkPanel.removeFromPanel(fireworkSpine, false);
            this.addChildAt(fireworkSpine, this.getChildIndex(this.fireworkPanel) + i);
            // Reskin the symbol spine for the variant.
            GameSymbolUtil.setVariantSkin(fireworkSpine, variants[i]);
            // Recolour the filter for the variant.
            fireworkFilter.color = this.uiProperties.fireworkColours[variants[i]];
            // Clear the firework emitter in case it has been recovered and there are lingering particles.
            fireworkEmitter.cleanup();
            // Launch the firework after a staggered delay.
            const from = fireworkSpine.position.clone();
            const to = new Point(from.x + (i - (symbols.length - 1) / 2) * GameMathUtil.lerp(this.uiProperties.fireworkSlantMin, this.uiProperties.fireworkSlantMax, Math.random()), from.y - GameMathUtil.lerp(this.uiProperties.fireworkDistanceMin, this.uiProperties.fireworkDistanceMax, Math.random()));
            const startDelay = 0;
            const duration = 0.6;
            timeline.call(async () => {
                // Launch the firework and wait for it to reach its destination.
                await firework.launch(from, to, duration);
                // Move the firework spine to the top layer so it isn't overlapped by the border.
                this.fireworkPanel.removeFromPanel(fireworkSpine, false);
                this.addChild(fireworkSpine);
                if (i === symbols.length - 1) {
                    gsap.to(this.mouseBlocker, { alpha: 0, duration: 0.2 });
                    this.dispatchGame(GameEvent.WIN_LINE_FIREWORKS_COMPLETE);
                }
                // Wait for the firework to explode.
                this.dispatchGame(GameSoundEvent.WIN_LINE_FIREWORK);
                await firework.detonate();
            }, [], startDelay);
        }
        return timeline;
    }
    /**
     * Determine the symbols to display for the fireworks.
     * - If the win line has numbers, it will show only that number variant for the whole line.
     * - If the win line is just jokers, it will show random symbols.
     */
    getFireworkSymbolsToDisplay(winLine) {
        return winLine.getSymbolType().isNumber()
            ? this.getMatchingNumberSymbolsToDisplay(winLine)
            : this.getRandomNumberSymbolsToDisplay(winLine);
    }
    /**
     * Get matching number symbols to display for a number win line.
     */
    getMatchingNumberSymbolsToDisplay(winLine) {
        const symbols = this.model.getWinLineSymbols(winLine);
        const numberSymbol = symbols.find(symbol => symbol.isNumber());
        if (!numberSymbol) {
            throw new Error("No number symbol found in win line symbols.");
        }
        const variant = GameSymbolUtil.numberToVariant(parseInt(numberSymbol.getId(), 10));
        return symbols
            .map((_, i) => GameSymbolUtil.variantToNumber(i, variant))
            .map((number) => SymbolConfigData.getSymbol(number.toString()));
    }
    /**
     * Get random number symbols to display for a joker win line, with no duplicates.
     */
    getRandomNumberSymbolsToDisplay(winLine) {
        const symbols = this.model.getWinLineSymbols(winLine);
        const variants = Array.from({ length: GameSymbolUtil.SYMBOL_VARIANTS }, (_, i) => i);
        ArrayUtil.shuffle(variants);
        return symbols
            .map((_, i) => GameSymbolUtil.variantToNumber(i, variants[i]))
            .map((number) => SymbolConfigData.getSymbol(number.toString()));
    }
}
//# sourceMappingURL=GameWinLineCelebration.js.map